// COO IA - Tableau de bord de pilotage des flux
// Le COO supervise les flux, escalades et anomalies - Il ne parle jamais aux clients

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Bell,
  BellOff,
  CheckCircle,
  Clock,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  RefreshCw,
  Settings,
  TrendingUp,
  Volume2,
  VolumeX,
  XCircle,
  Zap,
  Filter,
  MessageSquare
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { ScrollArea } from "../../components/ui/scroll-area";
import { toast } from "sonner";
import CxOChatPanel from "./CxOChatPanel";
import WhatsAppSuggestionsWidget from "../operation/WhatsAppSuggestionsWidget";
import useWhatsAppNotifications from "../../hooks/useWhatsAppNotifications";

const API = process.env.REACT_APP_BACKEND_URL;

// Helpers
const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return { Authorization: `Bearer ${token}` };
};

// Composant KPI Card
const KPICard = ({ icon: Icon, label, value, trend, trendDirection, color, subtitle }) => {
  const colorClasses = {
    blue: "from-blue-500/20 to-cyan-500/20 border-blue-500/30",
    amber: "from-amber-500/20 to-orange-500/20 border-amber-500/30",
    emerald: "from-emerald-500/20 to-green-500/20 border-emerald-500/30",
    red: "from-red-500/20 to-rose-500/20 border-red-500/30"
  };

  const iconColors = {
    blue: "text-blue-400",
    amber: "text-amber-400",
    emerald: "text-emerald-400",
    red: "text-red-400"
  };

  return (
    <Card className={`bg-gradient-to-br ${colorClasses[color]} border`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-zinc-400 mb-1">{label}</p>
            <p className="text-2xl font-bold text-white">{value}</p>
            {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className={`w-10 h-10 rounded-lg bg-black/20 flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${iconColors[color]}`} />
            </div>
            {trend && (
              <div className={`flex items-center gap-1 text-xs ${
                trendDirection === "up" ? "text-emerald-400" : 
                trendDirection === "down" ? "text-red-400" : "text-zinc-400"
              }`}>
                {trendDirection === "up" ? <ArrowUpRight className="w-3 h-3" /> :
                 trendDirection === "down" ? <ArrowDownRight className="w-3 h-3" /> : null}
                {trend}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Composant Channel Bar
const ChannelBar = ({ icon: Icon, name, count, percent, color }) => {
  const colorClasses = {
    blue: "bg-blue-500",
    emerald: "bg-emerald-500",
    purple: "bg-purple-500",
    zinc: "bg-zinc-500"
  };

  return (
    <div className="flex items-center gap-3 py-2">
      <div className={`w-8 h-8 rounded-lg ${colorClasses[color]}/20 flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${colorClasses[color].replace("bg-", "text-")}`} />
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-white">{name}</span>
          <span className="text-sm text-zinc-400">{count}</span>
        </div>
        <Progress value={percent} className="h-1.5" />
      </div>
    </div>
  );
};

// Composant State Distribution
const StateDistribution = ({ states }) => {
  const stateConfig = {
    init: { label: "Init", color: "bg-zinc-500" },
    collecting: { label: "Collecte", color: "bg-blue-500" },
    confirming: { label: "Confirmation", color: "bg-amber-500" },
    processing: { label: "Traitement", color: "bg-purple-500" },
    escalating: { label: "Escalade", color: "bg-red-500" },
    done: { label: "Terminé", color: "bg-emerald-500" }
  };

  return (
    <div className="space-y-2">
      {Object.entries(states || {}).map(([state, data]) => {
        const config = stateConfig[state] || { label: state, color: "bg-zinc-500" };
        return (
          <div key={state} className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${config.color}`}></div>
            <span className="text-xs text-zinc-400 w-20">{config.label}</span>
            <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <div 
                className={`h-full ${config.color} transition-all`} 
                style={{ width: `${data?.percent || 0}%` }}
              ></div>
            </div>
            <span className="text-xs text-zinc-500 w-10 text-right">{data?.percent || 0}%</span>
          </div>
        );
      })}
    </div>
  );
};

// Composant Escalation Rule
const EscalationRule = ({ rule }) => {
  const priorityColor = rule.priority >= 80 ? "text-red-400" : 
                        rule.priority >= 50 ? "text-amber-400" : "text-zinc-400";

  return (
    <div className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${
          rule.priority >= 80 ? "bg-red-500" : 
          rule.priority >= 50 ? "bg-amber-500" : "bg-zinc-500"
        }`}></div>
        <span className="text-sm text-white">{rule.rule_id}</span>
      </div>
      <div className="flex items-center gap-3">
        <Badge variant="outline" className={priorityColor}>P{rule.priority}</Badge>
        <span className="text-sm font-medium text-white">×{rule.count}</span>
      </div>
    </div>
  );
};

// Composant Anomaly Alert
const AnomalyAlert = ({ anomaly, onAcknowledge }) => {
  const severityConfig = {
    critical: { bg: "bg-red-500/10", border: "border-red-500/30", icon: XCircle, color: "text-red-400" },
    warning: { bg: "bg-amber-500/10", border: "border-amber-500/30", icon: AlertTriangle, color: "text-amber-400" },
    info: { bg: "bg-blue-500/10", border: "border-blue-500/30", icon: Activity, color: "text-blue-400" }
  };

  const config = severityConfig[anomaly.severity] || severityConfig.info;
  const Icon = config.icon;

  return (
    <div className={`${config.bg} ${config.border} border rounded-lg p-3 mb-2`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-4 h-4 ${config.color} mt-0.5`} />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <p className="text-sm text-white">{anomaly.message}</p>
            {!anomaly.acknowledged && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-xs"
                onClick={() => onAcknowledge(anomaly.id)}
              >
                <Eye className="w-3 h-3 mr-1" />
                Vu
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-zinc-500">
              {new Date(anomaly.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </span>
            {anomaly.agent_id && (
              <Badge variant="outline" className="text-xs">{anomaly.agent_id}</Badge>
            )}
            {anomaly.channel && (
              <Badge variant="outline" className="text-xs">{anomaly.channel}</Badge>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant principal
export default function COODashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState("1h");
  const [data, setData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");

  const navigateToTickets = useCallback(() => {
    window.dispatchEvent(new CustomEvent("omnihub:navigate", {
      detail: { hub: "operation", item: "tickets-cxo" }
    }));
  }, []);

  const { soundEnabled, pushEnabled, toggleSound, togglePush } = useWhatsAppNotifications({
    onNavigateToTickets: navigateToTickets,
    enabled: true,
  });

  const fetchData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    
    try {
      const response = await axios.get(`${API}/api/core/coo/dashboard?period=${period}`, {
        headers: getAuthHeaders()
      });
      setData(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching COO dashboard:", error);
      // Aucune donnée fictive - on affiche null pour déclencher l'état "indisponible"
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
    
    // Rafraîchissement automatique toutes les 30 secondes
    const interval = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAcknowledge = async (anomalyId) => {
    try {
      await axios.post(`${API}/api/core/coo/anomalies/${anomalyId}/ack`, 
        { user: "admin" },
        { headers: getAuthHeaders() }
      );
      fetchData();
    } catch (error) {
      console.error("Error acknowledging anomaly:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  const channels = data?.channels?.channels || {};
  const totalChannels = data?.channels?.total || 0;

  return (
    <div className="h-full" data-testid="coo-dashboard">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-zinc-900/50 border border-white/10">
            <TabsTrigger 
              value="dashboard" 
              className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400"
            >
              <Activity className="w-4 h-4 mr-2" />
              Tableau de bord
            </TabsTrigger>
            <TabsTrigger 
              value="chat" 
              className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Mode Conversationnel
            </TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-3">
            {/* Période */}
            <div className="flex items-center bg-zinc-900 rounded-lg p-1">
              {["1h", "24h", "7d"].map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant={period === p ? "secondary" : "ghost"}
                  className="h-7 px-3 text-xs"
                  onClick={() => setPeriod(p)}
                >
                  {p}
                </Button>
              ))}
            </div>
            
            {/* Notifications WhatsApp */}
            <div className="flex items-center gap-1 bg-zinc-900 rounded-lg p-1">
              <button
                data-testid="wa-toggle-sound"
                onClick={toggleSound}
                title={soundEnabled ? "Désactiver le son" : "Activer le son"}
                className={`h-7 w-7 rounded flex items-center justify-center transition-colors ${
                  soundEnabled ? "bg-emerald-500/20 text-emerald-400" : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {soundEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
              </button>
              <button
                data-testid="wa-toggle-push"
                onClick={togglePush}
                title={pushEnabled ? "Désactiver les notifications" : "Activer les notifications navigateur"}
                className={`h-7 w-7 rounded flex items-center justify-center transition-colors ${
                  pushEnabled ? "bg-emerald-500/20 text-emerald-400" : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                {pushEnabled ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
              </button>
            </div>

            {/* Rafraîchir */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchData(true)}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
          </div>
        </div>

        {/* TAB: Dashboard */}
        <TabsContent value="dashboard" className="flex-1 mt-0 space-y-6">
          {/* État: Données indisponibles */}
          {!data && (
            <Card className="bg-zinc-900/50 border-amber-500/30">
              <CardContent className="p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Données indisponibles</h3>
                <p className="text-zinc-400 text-sm mb-4">
                  L'endpoint COO n'est pas disponible. Aucune donnée fictive n'est affichée.
                </p>
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                  Module en attente de données réelles
                </Badge>
              </CardContent>
            </Card>
          )}

          {/* Contenu normal si données disponibles */}
          {data && (
            <>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                <Settings className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  Tableau de bord
                  <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">COO IA</Badge>
                </h1>
                <p className="text-zinc-400 text-sm">Pilotage des flux & opérations en temps réel</p>
              </div>
            </div>
            {lastUpdate && (
              <p className="text-xs text-zinc-500">
                Dernière mise à jour: {lastUpdate.toLocaleTimeString("fr-FR")}
              </p>
            )}
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard
              icon={Activity}
              label="Interactions"
              value={data?.kpis?.total_interactions || 0}
              trend="+23%"
              trendDirection="up"
              color="blue"
              subtitle={`Période: ${period}`}
            />
        <KPICard
          icon={Zap}
          label="Sessions actives"
          value={data?.kpis?.active_sessions || 0}
          color="emerald"
          subtitle="En cours"
        />
        <KPICard
          icon={TrendingUp}
          label="Taux escalade"
          value={`${data?.kpis?.escalation_rate || 0}%`}
          trend={data?.escalations?.trend || "0%"}
          trendDirection={data?.escalations?.trend?.startsWith("-") ? "down" : "up"}
          color="amber"
        />
        <KPICard
          icon={AlertTriangle}
          label="Anomalies"
          value={data?.kpis?.anomaly_count || 0}
          color={data?.kpis?.anomaly_count > 0 ? "red" : "emerald"}
          subtitle={data?.kpis?.anomaly_count > 0 ? "À traiter" : "Aucune"}
        />
      </div>

      {/* Grille principale */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {/* Widget WhatsApp Incidents Terrain */}
        <WhatsAppSuggestionsWidget
          onNavigateToTickets={navigateToTickets}
        />

        {/* Flux par canal */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-400" />
              Flux par canal
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <ChannelBar
              icon={Phone}
              name="Voice"
              count={channels.voice?.active || 0}
              percent={totalChannels > 0 ? (channels.voice?.active / totalChannels) * 100 : 0}
              color="blue"
            />
            <ChannelBar
              icon={MessageCircle}
              name="SMS"
              count={channels.sms?.active || 0}
              percent={totalChannels > 0 ? (channels.sms?.active / totalChannels) * 100 : 0}
              color="emerald"
            />
            <ChannelBar
              icon={Mail}
              name="Email"
              count={channels.email?.active || 0}
              percent={totalChannels > 0 ? (channels.email?.active / totalChannels) * 100 : 0}
              color="purple"
            />
            <ChannelBar
              icon={Zap}
              name="API"
              count={channels.api?.active || 0}
              percent={totalChannels > 0 ? (channels.api?.active / totalChannels) * 100 : 0}
              color="zinc"
            />
          </CardContent>
        </Card>

        {/* Distribution des états */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-400" />
              Distribution des états
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StateDistribution states={data?.states?.states} />
            <div className="mt-3 pt-3 border-t border-zinc-800">
              <div className="flex items-center justify-between text-xs">
                <span className="text-zinc-400">Total actif</span>
                <span className="text-white font-medium">{data?.states?.total_active || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Escalades */}
        <Card className="bg-zinc-900/50 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                Escalades actives
              </div>
              <Badge variant="outline" className="text-xs">
                {data?.escalations?.total || 0} total
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(data?.escalations?.by_rule || []).length > 0 ? (
              <div>
                {data.escalations.by_rule.map((rule, idx) => (
                  <EscalationRule key={idx} rule={rule} />
                ))}
                <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between text-xs">
                  <span className="text-zinc-400">Taux global</span>
                  <span className="text-amber-400 font-medium">{data?.escalations?.rate_percent || 0}%</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">Aucune escalade active</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Anomalies & Alertes */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Anomalies & Alertes
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" className="h-7 px-2">
                <Filter className="w-3 h-3 mr-1" />
                Filtrer
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data?.anomalies || []).length > 0 ? (
            <div>
              {data.anomalies.map((anomaly, idx) => (
                <AnomalyAlert 
                  key={anomaly.id || idx} 
                  anomaly={anomaly} 
                  onAcknowledge={handleAcknowledge}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
              <p className="text-white font-medium">Aucune anomalie détectée</p>
              <p className="text-xs text-zinc-500 mt-1">Tous les systèmes fonctionnent normalement</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Note COO */}
      <div className="text-center text-xs text-zinc-600 py-2">
        COO IA • Pilotage des flux, escalades et anomalies • Pas d&apos;interaction client
      </div>
            </>
          )}
        </TabsContent>

        {/* TAB: Mode Conversationnel (Chat) */}
        <TabsContent value="chat">
          <CxOChatPanel cxoType="coo" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
